import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import useMobile from '../hooks/useMobile';

export default function DeleteAccountPage() {
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState('');
  const { logout, user }        = useAuth();
  const isMobile                = useMobile();

  const deleteAccount = async () => {
    if (confirm !== 'DELETE') {
      setError('Skriv DELETE (store bokstaver) for å bekrefte');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/auth/delete-account', {
        headers: { Authorization: `Bearer ${token}` }
      });
      logout();
    } catch (err) {
      setError(err.response?.data?.error || 'Feil ved sletting av konto');
      setLoading(false);
    }
  };

  const DATA_ITEMS = [
    { icon: '👤', text: 'Din brukerprofil og innloggingsinformasjon' },
    { icon: '📅', text: 'Dine kalender-hendelser' },
    { icon: '💬', text: 'Dine chat-meldinger' },
    { icon: '✅', text: 'Dine oppgaver og huskelister' },
    { icon: '🔗', text: 'Tilkoblede Google og Facebook kontoer' },
    { icon: '📧', text: 'Dine e-post kontoer i appen' },
    { icon: '🔔', text: 'Push-varsler og varslingspreferanser' },
  ];

  const containerStyle = isMobile
    ? { background: '#F5F5F5', minHeight: '100%', fontFamily: "'Plus Jakarta Sans',sans-serif" }
    : { maxWidth: 540, margin: '40px auto', padding: '0 20px 40px' };

  const inner = isMobile
    ? { padding: '20px 14px' }
    : {};

  return (
    <div style={containerStyle}>
      {/* Mobil-header */}
      {isMobile && (
        <div style={{ background: '#fff', padding: '16px 16px 14px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', padding: 0, lineHeight: 1 }}>←</button>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#1A1A35', margin: 0 }}>Slett konto</h1>
        </div>
      )}

      <div style={inner}>
        {/* Desktop tittel */}
        {!isMobile && (
          <div style={{ marginBottom: 24 }}>
            <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              ← Tilbake
            </button>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--red)', margin: 0 }}>🗑️ Slett konto</h2>
            <p style={{ fontSize: 14, color: 'var(--txm)', marginTop: 6 }}>
              Hei {user?.name} — dette vil permanent fjerne kontoen din fra Famora.
            </p>
          </div>
        )}

        {/* Advarselskort */}
        <div style={{
          background: isMobile ? '#fff' : 'var(--red-bg)',
          border: `1px solid ${isMobile ? '#FCA5A5' : 'var(--red)'}`,
          borderRadius: 14,
          padding: '16px 18px',
          marginBottom: 16,
          ...(isMobile ? { boxShadow: '0 1px 4px rgba(0,0,0,.05)' } : {}),
        }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: isMobile ? '#DC2626' : 'var(--red)', marginBottom: 12 }}>
            ⚠️ Dette fjernes permanent:
          </div>
          {DATA_ITEMS.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < DATA_ITEMS.length - 1 ? `1px solid ${isMobile ? '#FEE2E2' : 'var(--red-bg)'}` : 'none' }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: 14, color: isMobile ? '#7F1D1D' : 'var(--red)' }}>{item.text}</span>
            </div>
          ))}
          <div style={{ marginTop: 14, padding: '10px 14px', background: isMobile ? '#FEE2E2' : 'rgba(220,38,38,.12)', borderRadius: 10, fontSize: 13, fontWeight: 700, color: isMobile ? '#DC2626' : 'var(--red)', textAlign: 'center' }}>
            ⛔ Dette kan IKKE angres!
          </div>
        </div>

        {/* Familie-advarsel */}
        <div style={{ background: isMobile ? '#FFF8E1' : 'var(--yellow-bg)', border: `1px solid ${isMobile ? '#FBBF24' : 'var(--yellow)'}`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: isMobile ? '#78350F' : 'var(--yellow)', lineHeight: 1.6 }}>
          💡 <strong>Merk:</strong> Familiens felles data (kalender, handleliste, matplan) beholdes for andre familiemedlemmer. Kun dine egne data fjernes.
        </div>

        {/* Bekreft-felt */}
        <div style={{ background: isMobile ? '#fff' : 'var(--card)', borderRadius: 14, padding: '16px', marginBottom: 16, ...(isMobile ? { boxShadow: '0 1px 4px rgba(0,0,0,.05)' } : { border: '1px solid var(--bdr)' }) }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 700, color: isMobile ? '#1A1A35' : 'var(--txt)', marginBottom: 10 }}>
            Skriv <span style={{ fontFamily: 'monospace', background: '#FEE2E2', color: '#DC2626', padding: '2px 6px', borderRadius: 6 }}>DELETE</span> for å bekrefte:
          </label>
          <input
            value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(''); }}
            placeholder="DELETE"
            autoComplete="off"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 12,
              border: `2px solid ${confirm === 'DELETE' ? '#DC2626' : '#E8E8F0'}`,
              background: isMobile ? '#F8F8FB' : 'var(--input-bg)',
              color: isMobile ? '#1A1A35' : 'var(--txt)',
              fontSize: 16,
              fontFamily: 'monospace',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color .2s',
            }}
          />
          {error && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#DC2626', fontWeight: 600 }}>
              {error}
            </div>
          )}
        </div>

        {/* Knapper */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={deleteAccount}
            disabled={confirm !== 'DELETE' || loading}
            style={{
              flex: 1,
              padding: '14px',
              borderRadius: 12,
              border: 'none',
              background: confirm === 'DELETE' ? '#DC2626' : (isMobile ? '#E8E8E8' : 'var(--bdr)'),
              color: confirm === 'DELETE' ? '#fff' : (isMobile ? '#aaa' : 'var(--txl)'),
              fontWeight: 800,
              fontSize: 15,
              cursor: confirm === 'DELETE' ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              transition: 'background .2s',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '⏳ Sletter...' : '🗑️ Slett min konto'}
          </button>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '14px 20px',
              borderRadius: 12,
              border: `1.5px solid ${isMobile ? '#E0E0E0' : 'var(--bdr)'}`,
              background: isMobile ? '#fff' : 'var(--card)',
              color: isMobile ? '#555' : 'var(--txm)',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}
